import React, { useState } from "react";
import { MapPin, Phone, Clock, Instagram } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const About = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at (336) 269-0356.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="about-page">
      <section className="lf-container py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-7">
          <span className="lf-eyebrow">Our story</span>
          <h1
            className="font-serif mt-4 leading-[0.95]"
            style={{
              fontSize: "clamp(2.75rem, 6vw, 5rem)",
              fontWeight: 300,
              color: "var(--lf-ink)",
            }}
          >
            A modern florist
            <br />
            <em style={{ color: "var(--lf-gold-deep)" }}>built on five years of love.</em>
          </h1>
          <p className="font-script text-3xl mt-5" style={{ color: "var(--lf-pink-deep)" }}>
            Luxury in every bloom.
          </p>

          <div className="mt-8 space-y-5 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--lf-ink-soft)" }}>
            <p>
              La Fleur Flower Shop officially opened its doors on <strong>February 13, 2026</strong>,
              the work of our owner and lead designer, <strong>Angy Rivas</strong> — a florist
              with over <strong>five years of experience</strong> designing for the people and
              events of North Carolina.
            </p>
            <p>
              We're a boutique studio with a clear point of view: we lean into the
              <em> luxury side of florals</em>. Lush, full, unmistakably elegant — and
              always finished with a modern twist. Whether it's a single hand-tied
              bouquet or a fifty-foot ceremony arch, every piece that leaves our shop
              is designed to feel intentional, generous, and unforgettable.
            </p>
            <p>
              We design for every chapter of your life — <strong>weddings, funerals,
              birthdays, anniversaries, brand events, and the just-because moments
              in between</strong>. Walk in for a quick bouquet and discover our
              Balloon Bar, our Flower Bar, our gifts, sweets, and more.
            </p>
            <p>
              We're proud to call <strong>Graham, North Carolina</strong> home — and even
              prouder of the team and the community that have grown alongside us.
            </p>
          </div>

          <div
            className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8"
            data-testid="contact-info"
          >
            <div>
              <MapPin size={20} strokeWidth={1.5} style={{ color: "var(--lf-gold-deep)" }} />
              <h3 className="font-serif text-2xl mt-3" style={{ color: "var(--lf-ink)" }}>
                Find us
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--lf-ink-soft)" }}>
                116 South Main St
                <br />
                Graham, NC
              </p>
            </div>
            <div>
              <Phone size={20} strokeWidth={1.5} style={{ color: "var(--lf-gold-deep)" }} />
              <h3 className="font-serif text-2xl mt-3" style={{ color: "var(--lf-ink)" }}>
                Call us
              </h3>
              <p className="mt-2 text-sm" style={{ color: "var(--lf-ink-soft)" }}>
                <a
                  href="tel:+13362690356"
                  className="hover:text-[var(--lf-gold-dark)]"
                  data-testid="about-phone"
                >
                  (336) 269-0356
                </a>
              </p>
            </div>
            <div>
              <Clock size={20} strokeWidth={1.5} style={{ color: "var(--lf-gold-deep)" }} />
              <h3 className="font-serif text-2xl mt-3" style={{ color: "var(--lf-ink)" }}>
                Hours
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--lf-ink-soft)" }}>
                Mon – Fri · 10a – 6p
                <br />
                Sat · 10a – 4p
                <br />
                Sun · Closed
              </p>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-5">
            <a
              href="https://instagram.com/la_fleur_floreria"
              target="_blank"
              rel="noreferrer"
              data-testid="about-instagram"
              className="lf-link inline-flex items-center gap-2"
            >
              <Instagram size={16} strokeWidth={1.5} />
              @la_fleur_floreria
            </a>
            <a
              href="https://tiktok.com/@la_fleur_floreria"
              target="_blank"
              rel="noreferrer"
              data-testid="about-tiktok"
              className="lf-link"
            >
              TikTok →
            </a>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="inquiry-form"
          className="lg:col-span-5 p-8 md:p-10"
          style={{
            background: "var(--lf-surface)",
            border: "1px solid var(--lf-border)",
          }}
        >
          <span className="lf-eyebrow">Send a note</span>
          <h2
            className="font-serif mt-3 leading-none"
            style={{
              fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
              fontWeight: 300,
              color: "var(--lf-ink)",
            }}
          >
            Custom orders & events
          </h2>

          <div className="mt-6 space-y-2">
            <label className="block">
              <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Name</span>
              <input
                data-testid="inquiry-name"
                className="lf-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
              />
            </label>
            <label className="block">
              <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Email</span>
              <input
                data-testid="inquiry-email"
                type="email"
                className="lf-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
              />
            </label>
            <label className="block">
              <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Phone (optional)</span>
              <input
                data-testid="inquiry-phone"
                className="lf-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(336) 000-0000"
              />
            </label>
            <label className="block">
              <span className="lf-eyebrow" style={{ fontSize: "0.65rem" }}>Tell us about your event</span>
              <textarea
                data-testid="inquiry-message"
                rows={4}
                className="lf-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Date, palette, vision…"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="inquiry-submit"
            className="lf-btn-primary mt-6 w-full justify-center"
          >
            {submitting ? "Sending…" : "Send inquiry"}
          </button>
        </form>
      </section>
    </div>
  );
};

export default About;
